import { Input, type InputProps } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/cn';
import { isEmpty } from 'lodash';
import { Info } from 'lucide-react';
import { Controller, useFormContext } from 'react-hook-form';

interface FormInputProps extends InputProps {
  name: string;
  label?: string;
  formWrapperClassName?: string;
  optional?: boolean;
  required?: boolean;
  tooltip?: string;
  onValueChange?: (value: string) => void;
  // Phone fields normally strip everything but digits and "+". Set this to also
  // allow spaces and dashes so numbers can be displayed like "085 - 800 1686".
  allowPhoneSeparators?: boolean;
  // 'inside' tucks the label into the box above the value (the auth screens);
  // 'above' is the default everywhere else.
  labelPlacement?: 'above' | 'inside';
}

function FormInput({
  name = '',
  label,
  formWrapperClassName,
  optional = false,
  required = false,
  tooltip,
  onValueChange,
  allowPhoneSeparators = false,
  labelPlacement = 'above',
  ...props
}: React.ComponentProps<'input'> & FormInputProps) {
  const form = useFormContext();

  const hasLabel = !isEmpty(label);
  const isInsideLabel = labelPlacement === 'inside';

  const labelContent = (
    <>
      {label}
      {optional && (
        <span className="font-outfit text-text-muted text-xs">(Optional)</span>
      )}
      {required && <span className="text-feedback-error">*</span>}
      {tooltip && (
        <Tooltip>
          <TooltipTrigger>
            <Info className="size-3.5" />
          </TooltipTrigger>
          <TooltipContent className="text-text-tertiary text-sm drop-shadow-lg">
            {tooltip}
          </TooltipContent>
        </Tooltip>
      )}
    </>
  );

  return (
    <Controller
      control={form.control}
      name={name}
      render={({ field, fieldState }) => (
        <div
          className={cn(
            'flex h-min w-full flex-col gap-1.5',
            formWrapperClassName
          )}
        >
          {hasLabel && !isInsideLabel && (
            <label
              htmlFor={name}
              className="text-text-secondary flex items-center gap-1 text-sm"
            >
              {labelContent}
            </label>
          )}
          <Input
            id={name}
            {...field}
            {...props}
            label={
              hasLabel && isInsideLabel ? (
                <span className="flex items-center gap-1">{labelContent}</span>
              ) : undefined
            }
            wrapperClassName={cn(
              fieldState.invalid &&
                'border-red-500 focus-within:border-red-600',
              props.wrapperClassName
            )}
            value={field.value ?? ''}
            onChange={e => {
              let value = e.target.value;
              // Only restrict for phone number fields
              if (name.toLowerCase().includes('phone')) {
                // Allow + and numbers, plus spaces/dashes when opted in
                value = allowPhoneSeparators
                  ? value.replace(/[^\d+\s-]/g, '')
                  : value.replace(/[^\d+]/g, '');
              }
              field.onChange(value);
              onValueChange?.(value);
            }}
          />
          {fieldState.invalid && fieldState.error?.message && (
            <span data-field-error className="text-xs text-red-500">
              {fieldState.error?.message}
            </span>
          )}
        </div>
      )}
    ></Controller>
  );
}

export { FormInput };
